import {
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { auth } from '@/lib/firebase'
import {
  idleExpired,
  localActivityStore,
  resolveSessionOnMount,
} from './idleTimeout'
import { isAllowlisted, rejectedSessionNeedsSignOut } from './allowlist'
import { setReportingIdentity } from '@/lib/problemReports'

export { isAllowlisted }

/**
 * Whether to sign in by redirect rather than by popup.
 *
 * A popup is the better desktop experience — the page keeps its state, and a
 * cancelled sign-in costs nothing. On a phone it is the opposite: the popup
 * opens as a separate in-app browser whose storage iOS partitions away from
 * the page that opened it, and the handoff then fails with
 *
 *   Unable to process request due to missing initial state.
 *
 * which is the message Matt was getting on roughly half his sign-ins. The
 * redirect keeps the whole flow in one browsing context, so there is no second
 * context whose storage can be taken away.
 *
 * **A redirect is only safe once the auth domain is this app's own domain**,
 * and the error above names that case itself: "using signInWithRedirect in a
 * storage-partitioned browser environment". Across sites a redirect fails
 * *every* time rather than half of them, so this checks the domain first and
 * stays with the popup until that is true. The check is deliberately in code
 * rather than in a deploy note — the two have to change together, and a note
 * cannot enforce that.
 */
function sameSiteAuthDomain(): boolean {
  const configured = auth.config.authDomain ?? ''
  return configured !== '' && configured === window.location.hostname
}

function prefersRedirect(): boolean {
  if (typeof window === 'undefined') return false
  if (!sameSiteAuthDomain()) return false
  const installed = window.matchMedia('(display-mode: standalone)').matches
  // `navigator.standalone` is iOS's own flag for a home-screen app, which is
  // how this is most often opened and the worst case for popups.
  const iosInstalled = (navigator as { standalone?: boolean }).standalone === true
  const touchPhone = window.matchMedia('(pointer: coarse)').matches
  return installed || iosInstalled || touchPhone
}

/**
 * Authentication — REQUIREMENTS.md §9.
 *
 * Google sign-in against the district Workspace account. Enabling that
 * provider means any Google account can authenticate, so the allowlist below
 * decides who may actually use the app.
 *
 * The allowlist check itself lives in `./allowlist` (firebase-free, so it can
 * be tested without initialising Firebase) and reads the shared list. It exists
 * only to show a clear message instead of a broken screen; the rule that
 * actually protects the data is in firestore.rules — a client check is a
 * courtesy, not a control.
 */

/**
 * True only in a dev server pointed at the local emulators. Both halves are
 * statically false in a production build, so everything guarded by this is
 * removed at build time and cannot reach a deployed bundle.
 */
export const EMULATOR_DEV_MODE =
  import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true'

/**
 * Sign in without a real Google account, for local development against the
 * emulators. `signInWithPopup` needs a genuine popup window, which automated
 * browsers and headless runs can't provide — this is the supported emulator
 * escape hatch: the Auth emulator accepts a JSON blob where a signed Google
 * ID token would normally go.
 *
 * Never available outside EMULATOR_DEV_MODE.
 */
async function signInAsEmulatorUser(email: string): Promise<void> {
  if (!EMULATOR_DEV_MODE) throw new Error('Emulator sign-in is dev-only')
  const credential = GoogleAuthProvider.credential(
    JSON.stringify({ sub: `emulator-${email}`, email, email_verified: true }),
  )
  await signInWithCredential(auth, credential)
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'not-allowed'; email: string }
  | { status: 'signed-in'; user: FirebaseUser }

interface AuthContextValue {
  state: AuthState
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  /** Dev-only; undefined unless running against the emulators. */
  signInAsEmulator?: ((email: string) => Promise<void>) | undefined
  error: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })
  const [error, setError] = useState<string | null>(null)
  // Set when we ourselves sign a rejected account out. The forced sign-out
  // fires a `null` auth event; this flag lets us keep the "not authorized"
  // notice on screen for that event instead of falling through to signed-out.
  const rejectedSignOut = useRef(false)

  /**
   * A redirect sign-in reports its outcome here, on the way back in.
   *
   * Without this a failed redirect lands silently on the sign-in screen, which
   * reads as "nothing happened" and invites a second attempt with no more
   * information than the first. A *successful* one needs nothing from us —
   * `onAuthStateChanged` below sees it — so only the failure is handled.
   */
  useEffect(() => {
    void getRedirectResult(auth).catch((cause: unknown) => {
      const code = (cause as { code?: string }).code ?? ''
      // Nothing was pending; this is an ordinary load, not a failure.
      if (code === 'auth/no-auth-event') return
      setError('That sign-in did not finish. Try it once more.')
    })
  }, [])

  useEffect(
    () =>
      onAuthStateChanged(auth, (user) => {
        if (user === null) {
          setReportingIdentity(null)
          if (rejectedSignOut.current) {
            rejectedSignOut.current = false
            return
          }
          setState({ status: 'signed-out' })
          return
        }

        // The check is a Firestore read now — the allowlist is data, so the
        // browser cannot answer it synchronously. Held on `checking` until it
        // resolves rather than showing the app and snatching it back, which
        // would flash a dashboard at someone being refused.
        setState({ status: 'loading' })
        void rejectedSessionNeedsSignOut(user.email).then((rejected) => {
          if (rejected) {
            // Do not leave a usable credential for an account this system does
            // not serve. Show the reason, then tear the session down now
            // rather than waiting for the user to press "sign in as someone
            // else" — and so a closed-and-reopened tab does not resume a
            // rejected session.
            setState({ status: 'not-allowed', email: user.email ?? '(no email)' })
            rejectedSignOut.current = true
            void firebaseSignOut(auth)
          } else {
            // Failures from here on are reportable, and they are reported
            // against this account. Set before the app renders, so the first
            // failing query on the first screen is not the one that is missed.
            setReportingIdentity({ userId: user.uid, email: user.email ?? '' })
            setState({ status: 'signed-in', user })
          }
        })
      }),
    [],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      error,
      async signIn() {
        setError(null)
        const provider = new GoogleAuthProvider()
        // Always show the account chooser. Without this, a personal Google
        // account cached in the browser gets reused silently and the sign-in
        // looks broken rather than wrong-account.
        provider.setCustomParameters({ prompt: 'select_account' })
        try {
          if (prefersRedirect()) {
            // Returns by navigating away; the result is picked up on load by
            // the effect below and by onAuthStateChanged.
            await signInWithRedirect(auth, provider)
            return
          }
          await signInWithPopup(auth, provider)
        } catch (cause) {
          const code = (cause as { code?: string }).code ?? ''
          if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
            return // User changed their mind; not an error worth showing.
          }
          setError(
            code === 'auth/popup-blocked'
              ? 'Your browser blocked the sign-in popup. Allow popups for this site and try again.'
              : 'Sign-in failed. Check your connection and try again.',
          )
        }
      },
      async signOut() {
        setError(null)
        // Clear the activity record so the next sign-in — possibly a different
        // account — does not inherit this one's idle clock and expire at once.
        localActivityStore.clear()
        rejectedSignOut.current = false
        await firebaseSignOut(auth)
        // Explicit, because from the "not authorized" screen the Firebase
        // session is already gone (torn down on rejection), so `firebaseSignOut`
        // fires no auth event to move us off it. This is what the screen's
        // "sign in with a different account" button relies on.
        setState({ status: 'signed-out' })
      },
      signInAsEmulator: EMULATOR_DEV_MODE ? signInAsEmulatorUser : undefined,
    }),
    [state, error],
  )

  useIdleSignOut(state.status === 'signed-in')

  return <AuthContext value={value}>{children}</AuthContext>
}

/**
 * Sign the user out after `IDLE_LIMIT_MS` of no interaction — see `idleTimeout`.
 *
 * Active only while signed in. It records activity on discrete input events
 * (not pointer *moves*, which would write constantly for nothing), and checks
 * for expiry three ways: on a slow interval, when the tab regains focus, and
 * when it becomes visible again. The last two matter most — a laptop lid closed
 * for the night reopens straight onto the check, and the closed-then-reopened
 * case is exactly what an idle timeout on a browser has to catch.
 */
function useIdleSignOut(active: boolean): void {
  const endIfExpired = useCallback(() => {
    if (idleExpired(localActivityStore.read(), Date.now())) {
      // Firebase's own sign-out, not the context's: this must run even though
      // the effect cannot see the context value, and clearing the record here
      // keeps a fresh sign-in from inheriting the stale clock.
      localActivityStore.clear()
      void firebaseSignOut(auth)
    }
  }, [])

  useEffect(() => {
    if (!active) return

    // Judge the stored time before overwriting it — the ordering lives in
    // `resolveSessionOnMount`, which records fresh activity only if the session
    // is still live.
    if (resolveSessionOnMount(localActivityStore, Date.now()).expired) {
      void firebaseSignOut(auth)
      return
    }

    const markActive = () => localActivityStore.write(Date.now())
    const onVisible = () => {
      if (document.visibilityState === 'visible') endIfExpired()
    }

    window.addEventListener('pointerdown', markActive)
    window.addEventListener('keydown', markActive)
    window.addEventListener('focus', endIfExpired)
    document.addEventListener('visibilitychange', onVisible)
    const interval = window.setInterval(endIfExpired, 60_000)

    return () => {
      window.removeEventListener('pointerdown', markActive)
      window.removeEventListener('keydown', markActive)
      window.removeEventListener('focus', endIfExpired)
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(interval)
    }
  }, [active, endIfExpired])
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return context
}

/**
 * The signed-in user, or throw. For use below <RequireAuth>, where being
 * signed in is already guaranteed — saves every consumer a null check.
 */
export function useCurrentUser(): FirebaseUser {
  const { state } = useAuth()
  if (state.status !== 'signed-in') {
    throw new Error('useCurrentUser must be used inside <RequireAuth>')
  }
  return state.user
}
